import React, { useEffect, useState, useRef } from "react";
import { Button, Form, Tabs, TabsProps, notification, Popconfirm } from "antd";
import { useParams } from "react-router-dom";
import { TbEditCircle } from "react-icons/tb";
import JoditEditor from "jodit-react";

import { editArticleContent, getArticleByID } from "../action";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  content: {
    en: string;
    ar: string;
    fr: string;
  };
}

const EditContent: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();

  const fetchItem = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const website = await getArticleByID(id);
      if (website) {
        form.setFieldsValue({
          content: {
            en: website?.Content?.En || "",
            ar: website?.Content?.Ar || "",
            fr: website?.Content?.Fr || "",
          },
        });
      }
    } catch (err) {
      api.error({ message: "Failed to fetch website" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await editArticleContent(id!, values.content);
      if (success) {
        fetchData();
        api.success({ message: "Content updated successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update content",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  const languages = ["en", "ar", "fr"] as const;
  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <Form.Item name={["content", lang]} label={`Content (${lang})`}>
        <JoditEditor
          ref={useRef(null)}
          value={form.getFieldValue(["content", lang])}
          onBlur={(newContent) => {
            form.setFieldValue(["content", lang], newContent);
          }}
          config={{
            readonly: false,
            direction: lang === "ar" ? "rtl" : "ltr",
            placeholder: `Write content in ${lang.toUpperCase()}...`,
            height: "50vh",
          }}
        />
      </Form.Item>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}
      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to edit the content?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<TbEditCircle />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Edit Content"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditContent;
